import { type VariantProps, cva } from "class-variance-authority";
import { AlertCircle, Search } from "lucide-react";
import * as React from "react";
import { Skeleton } from "../atoms/skeleton.js";
import { cx } from "../internal/cx.js";
import { SystemMessage } from "./system-message.js";

/**
 * Standardized empty / loading / error state inside data tables.
 *
 * Variants:
 *   - `empty` — no rows / no search results.
 *   - `loading` — skeleton rows while table data loads.
 *   - `error` — failed table load.
 */
const tableStateVariants = cva("cw-v2-table-state", {
  variants: {
    variant: {
      empty: "cw-v2-table-state--empty",
      loading: "cw-v2-table-state--loading",
      error: "cw-v2-table-state--error",
    },
  },
  defaultVariants: {
    variant: "empty",
  },
});

export type TableStateVariant = NonNullable<VariantProps<typeof tableStateVariants>["variant"]>;

export interface TableStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof tableStateVariants> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  loadingRows?: number;
}

export const TableState = React.forwardRef<HTMLDivElement, TableStateProps>(function TableState(
  { action, className, description, loadingRows = 3, title, variant = "empty", ...props },
  ref,
) {
  if (variant === "loading") {
    return (
      <div
        ref={ref}
        aria-label={typeof title === "string" ? title : "Loading table rows"}
        className={cx(tableStateVariants({ variant }), className)}
        // biome-ignore lint/a11y/useSemanticElements: the loading wrapper needs to lay out as a generic block with the table-state grid; `<output>` would also imply form-output semantics that don't apply here.
        role="status"
        {...props}
      >
        {Array.from({ length: loadingRows }).map((_, index) => (
          <Skeleton
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are positional placeholders
            key={index}
            className="cw-v2-table-state__skeleton-row"
          />
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className={cx(tableStateVariants({ variant }), className)} {...props}>
      <SystemMessage
        density="compact"
        description={description}
        illustration={variant === "error" ? <AlertCircle size={28} /> : <Search size={28} />}
        primaryAction={action}
        title={title ?? (variant === "error" ? "Could not load table data" : "No rows to show")}
        tone={variant === "error" ? "danger" : "neutral"}
      />
    </div>
  );
});
TableState.displayName = "TableState";
